import React from 'react';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import colors from 'src/styles/colors.module.scss';
import styles from './plaidLinkAccountsCallout.module.scss';
import { invalidateAllAccountRelatedQueries } from 'src/hooks/queries/accounts';
import Callout from 'src/components/atomic/atoms/callout/Callout';
import PlaidConnector from 'src/components/atomic/atoms/plaidConnector/PlaidConnector';
import { GlyphLinkOut } from '@americanexpress/dls-react';
import MoreInfoTooltip from 'src/components/more-info-tooltip/MoreInfoTooltip';
import { AMEX_PRIVACY_CENTER, DATA_CONNECTION_TERMS_AND_CONDITIONS } from 'src/utils/constants';
import { useQueryClient } from '@tanstack/react-query';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

export default function PlaidLinkAccountsCallout({ withFooter = false }: { withFooter?: boolean }): JSX.Element {
    const { isMinWidthMedium } = useBreakpoints();
    const queryClient = useQueryClient();
    const location = useLocation();
    const linkingAccountURL = process.env.REACT_APP_LINKING_ACCOUNTS;

    const onSuccess = async (): Promise<void> => {
        await invalidateAllAccountRelatedQueries(queryClient);
        const scrollContainer: HTMLElement = document.getElementById('scrollContainer');
        scrollContainer.scrollTo(0, 0);
    };

    return (
        <Callout withButton={false} className={styles.callout}>
            <div className={styles.headingRow}>
                <Typography
                    variant={TypographyVariants.HEADING_S}
                    component="span"
                    className={`${styles.header} ${colors.textStrong}`}
                >
                    {LiteralsService.getLiteral('Budget.Connect.callout.linkYourOtherFinancial')}
                    <span className={styles.noWrap}>
                        {LiteralsService.getLiteral('Budget.Connect.callout.accounts')}
                        <MoreInfoTooltip
                            placement={isMinWidthMedium ? TooltipPlacement.LEFT : TooltipPlacement.TOP}
                            className={styles.tooltip}
                            buttonAriaLabel={LiteralsService.getLiteral(
                                'Budget.Connect.callout.linkYourOtherFinancialAccounts'
                            )}
                            onClick={() => trackClick(location.pathname, adobeTags.Tooltip.LinkAccounts)}
                            body={
                                <div className={styles.tooltipBody}>
                                    <Typography
                                        variant={TypographyVariants.HEADING_XS}
                                        component="p"
                                        className={colors.brand}
                                    >
                                        {LiteralsService.getLiteral(
                                            'Budget.Connect.tooltip.externalFinancialAccountsInclude'
                                        )}
                                    </Typography>
                                    <ul>
                                        <li>
                                            {LiteralsService.getLiteral(
                                                'Budget.Connect.tooltip.consumerCheckingAccounts'
                                            )}
                                        </li>
                                        <li>
                                            {LiteralsService.getLiteral('Budget.Connect.tooltip.consumerCreditCards')}
                                        </li>
                                    </ul>
                                    <Anchor href={linkingAccountURL} tag={adobeTags.Tooltip.LinkAccountsArticle} inline>
                                        {LiteralsService.getLiteral(
                                            'Budget.Connect.tooltip.readMoreAboutConnectingAccounts'
                                        )}
                                        <GlyphLinkOut />
                                    </Anchor>
                                </div>
                            }
                        />
                    </span>
                </Typography>
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Budget.Connect.callout.securelyLinkYourAccounts')}
                </Typography>
            </div>
            <Typography variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Budget.Connect.callout.whenYourAccountsAreLinked')}
            </Typography>
            <ul>
                <li>
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('Budget.Connect.callout.youCanSeeYour')}
                    </Typography>
                </li>
                <li>
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('Budget.Connect.callout.youCanTrack')}
                    </Typography>
                </li>
                <li>
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('Budget.Connect.callout.yourDataIsEncrypted')}
                    </Typography>
                </li>
            </ul>
            <Typography variant={TypographyVariants.LEGAL_TERTIARY}>
                {LiteralsService.getLiteral('Budget.Connect.callout.termsAndConditionsText')}
                <Anchor href={DATA_CONNECTION_TERMS_AND_CONDITIONS} tag={adobeTags.AccountConnection.TC} inline>
                    {LiteralsService.getLiteral('Budget.dataConnectionTermsAndConditionsLink')}
                </Anchor>
                {LiteralsService.getLiteral('Budget.Connect.callout.privacyText')}
                <Anchor href={AMEX_PRIVACY_CENTER} tag={adobeTags.AccountConnection.LinkingArticle} inline>
                    {LiteralsService.getLiteral('Budget.Connect.callout.privacyLink')}
                </Anchor>
                {LiteralsService.getLiteral('Budget.Connect.callout.privacySecondaryText')}
            </Typography>
            <PlaidConnector onSuccessCallback={onSuccess} className={styles.plaidConnector}>
                {LiteralsService.getLiteral('Budget.Connect.agreeAndLinkAccount')}
            </PlaidConnector>
            {withFooter && (
                <Typography variant={TypographyVariants.SUBTEXT} className={styles.calloutFooter}>
                    {LiteralsService.getLiteral('Budget.Connect.callout.ifYouDontWant')}
                </Typography>
            )}
        </Callout>
    );
}
